import { Check, RotateCcw } from 'lucide-react';
import { useRef, useState, type CSSProperties } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router';

import { Button } from '@/components/Button/Button';
import { Card } from '@/components/Card/Card';

import { assignmentClassAverage, questionClassStats } from './assignmentQuestionStats';
import { GiveFullCreditDialog } from './GiveFullCreditDialog';
import { GradebookHeader } from './GradebookHeader';
import { GradesLoadFailed } from './GradesLoadFailed';
import type { GradebookAssignment, GradebookQuestionMeta, GradebookScoreKind } from './gradebookTypes';
import { buildQuestionMetaKey, formatGradeValue } from './gradebookTypes';
import { QuestionActionsMenu } from './QuestionActionsMenu';
import { RemoveQuestionDialog } from './RemoveQuestionDialog';
import { useGradebook } from './useGradebook';
import { useGiveQuestionFullCredit, useSetQuestionRemoved } from './useQuestionClassActions';
import styles from './AssignmentQuestionsPage.module.css';

// Fixed messages: the server's text is not written for instructors.
const CREDIT_ERROR = 'Unable to give full credit right now. Please try again.';
const REMOVE_ERROR = 'Unable to remove the question right now. Please try again.';
const RESTORE_ERROR = 'Unable to restore the question right now. Please try again.';

const KINDS: ReadonlyArray<{ heading: string; kind: GradebookScoreKind }> = [
  { heading: 'Pre-lecture questions', kind: 'preLecture' },
  { heading: 'Homework questions', kind: 'homework' },
];

interface PendingAction {
  kind: 'credit' | 'remove';
  question: GradebookQuestionMeta;
}

/** "Question 2", as students see it; a pre-lecture question says so. */
export function questionName(question: GradebookQuestionMeta) {
  const number = /^Q(\d+)$/.exec(question.label)?.[1];
  if (!number) {
    return question.label;
  }
  return question.kind === 'preLecture' ? `Pre-lecture question ${number}` : `Question ${number}`;
}

function questionsOf(assignment: GradebookAssignment, kind: GradebookScoreKind) {
  return assignment.sections.flatMap((section) =>
    kind === 'preLecture' ? section.preLectureQuestions : section.homeworkQuestions,
  );
}

function maxOf(assignment: GradebookAssignment, kind: GradebookScoreKind) {
  return kind === 'preLecture' ? assignment.preLectureMaxScore : assignment.homeworkMaxScore;
}

function percent(value: number, max: number) {
  return max > 0 ? Math.round((value / max) * 100) : 0;
}

function points(value: number) {
  return `${formatGradeValue(value)} ${value === 1 ? 'point' : 'points'}`;
}

function oneDecimal(value: number) {
  return formatGradeValue(Math.round(value * 10) / 10);
}

// What a removal changes the worth of: the assignment, or, when it has both
// kinds of question, the kind's share of it.
function totalOf(assignment: GradebookAssignment, kind: GradebookScoreKind) {
  if (!(assignment.hasHomework && assignment.hasPreLecture)) {
    return undefined;
  }
  return kind === 'preLecture' ? ('pre-lecture questions' as const) : ('homework' as const);
}

// How a removed question is named: its prompt, or its type when it has none.
function removedQuestionDescription(question: GradebookQuestionMeta) {
  if (question.prompt) {
    return question.prompt;
  }
  return question.typeLabel ? `${question.typeLabel} question` : 'Untitled question';
}

// Moves focus to a question's control once the page has updated: its actions
// button while it counts, its Restore button once removed. Falls back to
// another element when the question has neither. A removal itself sends
// focus to its confirmation instead (focusConfirmationNext).
function focusQuestionControl(key: string, fallback: HTMLElement | null) {
  window.setTimeout(() => {
    const control = Array.from(document.querySelectorAll<HTMLElement>('[data-question-control]')).find(
      (element) => element.dataset.questionControl === key,
    );
    (control ?? fallback)?.focus();
  }, 0);
}

export function AssignmentQuestionsPage() {
  const navigate = useNavigate();
  const { componentUid, courseId } = useParams();
  const gradebook = useGradebook(courseId);
  const giveFullCredit = useGiveQuestionFullCredit(courseId);
  const setRemoved = useSetQuestionRemoved(courseId);
  const [pending, setPending] = useState<PendingAction | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [announcement, setAnnouncement] = useState('');
  const [restoreError, setRestoreError] = useState<string | null>(null);
  const [restoringKey, setRestoringKey] = useState<string | null>(null);
  const pageRef = useRef<HTMLElement>(null);
  const confirmationRef = useRef<HTMLParagraphElement>(null);
  // Set once a removal is stored. Its dialog then puts focus on the
  // confirmation: the question's own control is now its Restore button, and
  // a second Enter there undid the removal (issue #112).
  const focusConfirmationNext = useRef(false);

  if (!courseId || !componentUid) {
    return (
      <section className={styles.page}>
        <Card className={styles.stateCard}>
          <h1 className={styles.stateTitle}>Missing assignment link</h1>
          <p className={styles.stateMessage}>
            The assignment route is incomplete, so the gradebook could not be loaded.
          </p>
        </Card>
      </section>
    );
  }

  if (!gradebook.courseQuery.isPending && gradebook.course && gradebook.course.userRole !== 'instructor') {
    return <Navigate replace to={`/app/courses/${courseId}`} />;
  }

  if (gradebook.courseQuery.isPending || gradebook.isLoadingRoster) {
    return (
      <section className={styles.page}>
        <Card className={styles.stateCard} role="status">
          <h1 className={styles.stateTitle}>Loading assignment</h1>
          <p className={styles.stateMessage}>Loading the assignment’s questions.</p>
        </Card>
      </section>
    );
  }

  // The full list, not just the grid's: an assignment whose questions were all
  // removed leaves the grid, but must stay reachable to restore them.
  const assignment = (gradebook.course?.gradebookAssignments ?? []).find(
    (candidate) => candidate.componentUid === componentUid && !candidate.isSectionDivider,
  );

  if (!gradebook.course || !assignment) {
    return (
      <section className={styles.page}>
        <Card className={styles.stateCard} role="alert">
          <h1 className={styles.stateTitle}>Assignment not found</h1>
          <p className={styles.stateMessage}>This assignment could not be found in the course.</p>
        </Card>
      </section>
    );
  }

  const students = gradebook.students;
  const studentCount = gradebook.studentCount || students.length;
  const gradesReady = gradebook.gradesQuery.data?.isComplete === true;
  // A failed load is said as such, not "Loading…" for ever.
  const gradesFailed = gradebook.gradesFailed;
  const groups = KINDS.map(({ heading, kind }) => ({
    counted: questionsOf(assignment, kind).filter((question) => question.isAssigned),
    heading,
    kind,
  })).filter((group) => group.counted.length > 0);
  const removed = KINDS.flatMap(({ kind }) => questionsOf(assignment, kind)).filter(
    (question) => question.isRemoved,
  );
  const hasBothKinds = assignment.hasHomework && assignment.hasPreLecture;
  const subtitle = [
    assignment.hasPreLecture ? `Pre-lecture · ${points(assignment.preLectureMaxScore)}` : null,
    assignment.hasHomework ? `Homework · ${points(assignment.homeworkMaxScore)}` : null,
    `${studentCount} ${studentCount === 1 ? 'student' : 'students'}`,
  ]
    .filter(Boolean)
    .join(' · ');

  function openAction(kind: PendingAction['kind'], question: GradebookQuestionMeta) {
    giveFullCredit.reset();
    setRemoved.reset();
    setPending({ kind, question });
    setDialogOpen(true);
  }

  async function confirmFullCredit(question: GradebookQuestionMeta) {
    try {
      await giveFullCredit.mutateAsync({ assignment: assignment!, courseId: courseId!, question });
    } catch {
      return; // The dialog stays open with its fixed message.
    }
    setDialogOpen(false);
    setAnnouncement(
      studentCount === 1
        ? `The 1 student now has full credit for ${questionName(question)}.`
        : `${studentCount} students now have full credit for ${questionName(question)}.`,
    );
  }

  async function confirmRemoval(question: GradebookQuestionMeta) {
    const newTotal = maxOf(assignment!, question.kind) - question.maxScore;
    try {
      await setRemoved.mutateAsync({ assignment: assignment!, courseId: courseId!, question, removed: true });
    } catch {
      return;
    }
    focusConfirmationNext.current = true;
    setDialogOpen(false);
    const part = totalOf(assignment!, question.kind);
    const subject = part ? `${assignment!.title}’s ${part}` : assignment!.title;
    setAnnouncement(`${questionName(question)} was removed. ${subject} ${part === 'pre-lecture questions' ? 'are' : 'is'} now worth ${points(newTotal)}.`);
  }

  async function restore(question: GradebookQuestionMeta) {
    const key = buildQuestionMetaKey(question);
    setRestoreError(null);
    setRestoringKey(key);
    try {
      await setRemoved.mutateAsync({ assignment: assignment!, courseId: courseId!, question, removed: false });
      setAnnouncement(`“${removedQuestionDescription(question)}” is back in the assignment.`);
      focusQuestionControl(key, pageRef.current);
    } catch {
      setRestoreError(RESTORE_ERROR);
    } finally {
      setRestoringKey(null);
    }
  }

  function renderQuestion(question: GradebookQuestionMeta) {
    const key = buildQuestionMetaKey(question);
    const stats = questionClassStats(assignment!, question, students);
    const everyoneFull = gradesReady && stats.studentCount > 0 && stats.fullCreditCount === stats.studentCount;
    const averagePercent = percent(stats.average, question.maxScore);
    const name = questionName(question);

    return (
      <li className={styles.questionRow} key={key}>
        <div className={styles.questionText}>
          <p className={styles.questionName}>
            {name}
            {question.typeLabel ? (
              <span className={styles.questionType}> · {question.typeLabel}</span>
            ) : null}
          </p>
          {question.prompt ? <p className={styles.questionPrompt}>{question.prompt}</p> : null}
        </div>
        <p className={styles.points}>
          <span className={styles.cellLabel}>Points </span>
          {formatGradeValue(question.maxScore)}
        </p>
        <div className={styles.average}>
          {gradesFailed ? (
            <p className={styles.secondary}>Grades not loaded</p>
          ) : !gradesReady ? (
            <p className={styles.secondary}>Loading grades…</p>
          ) : everyoneFull ? (
            <p className={styles.fullBadge}>
              <Check aria-hidden="true" className={styles.fullBadgeIcon} size={14} strokeWidth={3} />
              Everyone has full credit
            </p>
          ) : (
            <>
              <p className={styles.averageText}>
                <span className={styles.cellLabel}>Class average </span>
                {oneDecimal(stats.average)}{' '}
                <span className={styles.secondary}>({averagePercent}%)</span>
              </p>
              <span aria-hidden="true" className={styles.bar}>
                <span
                  className={styles.barFill}
                  style={{ '--average': `${averagePercent}%` } as CSSProperties}
                />
              </span>
            </>
          )}
        </div>
        <div className={styles.actions}>
          <QuestionActionsMenu
            canGiveFullCredit={!everyoneFull}
            controlKey={key}
            onGiveFullCredit={() => openAction('credit', question)}
            onRemove={() => openAction('remove', question)}
            questionName={name}
          />
        </div>
      </li>
    );
  }

  const pendingKey = pending ? buildQuestionMetaKey(pending.question) : '';
  const pendingStats = pending && gradesReady ? questionClassStats(assignment, pending.question, students) : null;
  const pendingCounted = pending
    ? questionsOf(assignment, pending.question.kind).filter((question) => question.isAssigned)
    : [];

  return (
    <section className={styles.page} ref={pageRef} tabIndex={-1}>
      <GradebookHeader
        backLabel="Back to Gradebook"
        onBack={() => navigate(`/app/courses/${courseId}/grades`)}
        subtitle={subtitle}
        title={assignment.title}
      />

      <Card className={styles.summaryCard} variant="elevated">
        {groups.map(({ kind }) => {
          const max = maxOf(assignment, kind);
          const average = assignmentClassAverage(assignment, kind, students);
          return (
            <div className={styles.summaryMetric} key={kind}>
              <p className={styles.summaryLabel}>
                {hasBothKinds ? (kind === 'preLecture' ? 'Pre-lecture class average' : 'Homework class average') : 'Class average'}
              </p>
              <p className={styles.summaryValue}>
                {gradesFailed
                  ? '—'
                  : gradesReady
                    ? `${oneDecimal(average)} / ${formatGradeValue(max)} (${percent(average, max)}%)`
                    : 'Loading…'}
              </p>
            </div>
          );
        })}
        <div className={styles.summaryMetric}>
          <p className={styles.summaryLabel}>Questions that count</p>
          <p className={styles.summaryValue}>
            {groups.reduce((total, group) => total + group.counted.length, 0)}
          </p>
        </div>
      </Card>

      {gradesFailed ? (
        <GradesLoadFailed error={gradebook.gradesError} onRetry={gradebook.retryGrades} />
      ) : null}

      {/* Shown as well as announced: the result of the last action. */}
      <p
        aria-atomic="true"
        aria-live="polite"
        className={styles.confirmation}
        ref={confirmationRef}
        tabIndex={-1}
      >
        {announcement}
      </p>

      {groups.length === 0 && removed.length === 0 ? (
        <Card className={styles.stateCard}>
          <p className={styles.stateMessage}>This assignment has no questions.</p>
        </Card>
      ) : null}

      {groups.map((group) => (
        <Card className={styles.group} key={group.kind}>
          <h2 className={styles.groupHeading}>{group.heading}</h2>
          <div aria-hidden="true" className={styles.listHeader}>
            <span>Question</span>
            <span>Points</span>
            <span>Class average</span>
            <span />
          </div>
          <ul className={styles.questionList}>
            {group.counted.map((question) => renderQuestion(question))}
          </ul>
        </Card>
      ))}

      {removed.length > 0 ? (
        <Card className={styles.group}>
          <h2 className={styles.groupHeading}>Removed from this assignment</h2>
          <p className={styles.secondary}>
            Students don’t see these questions, and they don’t count toward any score. Restoring one
            brings back every student’s answer and score.
          </p>
          <ul className={styles.removedList}>
            {removed.map((question) => {
              const key = buildQuestionMetaKey(question);
              const description = removedQuestionDescription(question);
              return (
                <li className={styles.removedRow} key={key}>
                  <p className={styles.removedText}>
                    <span className={styles.removedPrompt}>{description}</span>
                    <span className={styles.removedBadge}>Removed</span>
                  </p>
                  <Button
                    aria-label={`Restore “${description}”`}
                    className={styles.restoreButton}
                    data-question-control={key}
                    disabled={restoringKey !== null}
                    onClick={() => {
                      void restore(question);
                    }}
                    variant="secondary"
                  >
                    <RotateCcw aria-hidden="true" size={16} />
                    {restoringKey === key ? 'Restoring…' : 'Restore'}
                  </Button>
                </li>
              );
            })}
          </ul>
          {restoreError ? (
            <p className={styles.error} role="alert">
              {restoreError}
            </p>
          ) : null}
        </Card>
      ) : null}

      {pending ? (
        <>
          <GiveFullCreditDialog
            errorMessage={giveFullCredit.isError ? CREDIT_ERROR : null}
            isPending={giveFullCredit.isPending}
            onConfirm={() => {
              void confirmFullCredit(pending.question);
            }}
            onOpenChange={setDialogOpen}
            onRestoreFocus={() => focusQuestionControl(pendingKey, pageRef.current)}
            open={dialogOpen && pending.kind === 'credit'}
            points={pending.question.maxScore}
            questionName={questionName(pending.question)}
            studentCount={studentCount}
            unansweredCount={pendingStats ? pendingStats.unansweredCount : null}
          />
          <RemoveQuestionDialog
            assignmentTitle={assignment.title}
            errorMessage={setRemoved.isError && !restoringKey ? REMOVE_ERROR : null}
            isPending={setRemoved.isPending && restoringKey === null}
            laterQuestionsRenumber={
              pendingCounted.findIndex((question) => buildQuestionMetaKey(question) === pendingKey) <
              pendingCounted.length - 1
            }
            newTotal={maxOf(assignment, pending.question.kind) - pending.question.maxScore}
            onConfirm={() => {
              void confirmRemoval(pending.question);
            }}
            onOpenChange={setDialogOpen}
            onRestoreFocus={() => {
              if (focusConfirmationNext.current) {
                focusConfirmationNext.current = false;
                window.setTimeout(() => confirmationRef.current?.focus(), 0);
              } else {
                focusQuestionControl(pendingKey, pageRef.current);
              }
            }}
            open={dialogOpen && pending.kind === 'remove'}
            previousTotal={maxOf(assignment, pending.question.kind)}
            questionName={questionName(pending.question)}
            totalOf={totalOf(assignment, pending.question.kind)}
          />
        </>
      ) : null}
    </section>
  );
}
